import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import localforage from 'localforage';
import useLocalStorage from '@msales/shared/hooks/useLocalStorage';
import { useClientConfig } from './clientConfigContext';
import { useAuthContext } from './authContext';

const backgroundGpsStore = localforage.createInstance({
  name: 'backgroundGPS',
  storeName: 'coords',
});

const BACKGROUND_GPS_INTERVAL = 10 * 60 * 1000;
const BACKGROUND_GPS_MAX_AGE = 15 * 60 * 1000;

export const Context = createContext({});

export const Provider = (props) => {
  const { children } = props;

  const [searchValue, setSearchValue] = useState(null);
  const [cityFilter, setCityFilter] = useState(null);
  const { authUser } = useAuthContext();
  const userId = authUser?.id;

  useEffect(() => {
    setCityFilter(null);
  }, [userId]);

  const [customer, setCustomerInternal] = useLocalStorage('customer', null);

  const setCustomer = useCallback(
    (newCustomer) => {
      setCustomerInternal(newCustomer);
    },
    [setCustomerInternal],
  );
  const [discounts, setDiscounts] = useState({});
  const { getValue } = useClientConfig();
  const [dailyRoute, setDailyRoute] = useState(true);
  const [startTime, setStartTime] = useLocalStorage('startTime', null);
  const [ulistavanjeProducts, setUlistavanjeProducts] = useLocalStorage(
    'ulistavanjeProducts',
    [],
  );
  const [resultCount, setResultCount] = useState(0);
  const [isRemoteVisit, setIsRemoteVisit] = useLocalStorage(
    'isRemoteVisit',
    null,
  );
  const listRef = useRef(null);
  const [coordinates, setCoordinates] = useState(null);
  const backgroundCoordsRef = useRef(null);
  const lastCoordsUpdate = useRef(0);
  const lastCoordsAccuracy = useRef(Infinity);

  const customerDiscounts = useMemo(() => {
    return discounts;
  }, [discounts]);

  useEffect(() => {
    const loadDiscounts = async () => {
      if (getValue('customers.fetchDiscounts', false) && customer) {
        try {
          const customersStore = localforage.createInstance({
            name: 'Customers',
          });
          const allDiscounts = await customersStore.getItem(
            'customerDiscounts',
          );
          if (allDiscounts && allDiscounts[customer.id]) {
            setDiscounts(allDiscounts[customer.id]);
          } else {
            setDiscounts({});
          }
        } catch (err) {
          console.error('Error loading discounts', err);
          setDiscounts({});
        }
      } else {
        setDiscounts({});
      }
    };
    loadDiscounts();
  }, [customer]);

  useEffect(() => {
    backgroundGpsStore
      .getItem('lastCoords')
      .then((stored) => {
        if (stored && Date.now() - stored.timestamp < BACKGROUND_GPS_MAX_AGE) {
          backgroundCoordsRef.current = stored.coords;
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (customer || !navigator.geolocation) return;

    const fetchBackgroundCoords = () => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState !== 'visible'
      ) {
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, altitude, accuracy } = position.coords;
          const coords = { latitude, longitude, altitude, accuracy };
          backgroundCoordsRef.current = coords;
          backgroundGpsStore
            .setItem('lastCoords', { coords, timestamp: position.timestamp || Date.now() })
            .catch(() => {});
        },
        () => {},
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 },
      );
    };

    fetchBackgroundCoords();
    const intervalId = setInterval(fetchBackgroundCoords, BACKGROUND_GPS_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [customer]);

  useEffect(() => {
    if (!customer) {
      setCoordinates(null);
      lastCoordsUpdate.current = 0;
      lastCoordsAccuracy.current = Infinity;
      return;
    }

    if (backgroundCoordsRef.current && !coordinates) {
      setCoordinates(backgroundCoordsRef.current);
      lastCoordsUpdate.current = Date.now();
      lastCoordsAccuracy.current =
        backgroundCoordsRef.current.accuracy || Infinity;
    }

    if (!navigator.geolocation) return;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const now = Date.now();
        const isFirst = lastCoordsUpdate.current === 0;
        const timeSinceLast = now - lastCoordsUpdate.current;
        const isStale = timeSinceLast > 120000;
        const isMoreAccurate =
          position.coords.accuracy < lastCoordsAccuracy.current * 0.5 &&
          timeSinceLast > 15000;

        if (isFirst || isStale || isMoreAccurate) {
          setCoordinates(position.coords);
          lastCoordsUpdate.current = now;
          lastCoordsAccuracy.current = position.coords.accuracy;

          const { latitude, longitude, altitude, accuracy } = position.coords;
          backgroundGpsStore
            .setItem('lastCoords', {
              coords: { latitude, longitude, altitude, accuracy },
              timestamp: position.timestamp || now,
            })
            .catch(() => {});
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 120000 },
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [customer]);

  const customerContext = useMemo(
    () => ({
      searchValue,
      setSearchValue,
      cityFilter,
      setCityFilter,
      customer,
      setCustomer,
      customerDiscounts,
      listRef,
      resultCount,
      setResultCount,
      dailyRoute,
      setDailyRoute,
      startTime,
      setStartTime,
      ulistavanjeProducts,
      setUlistavanjeProducts,
      isRemoteVisit,
      setIsRemoteVisit,
      coordinates,
      setCoordinates,
    }),
    [
      searchValue,
      setSearchValue,
      cityFilter,
      setCityFilter,
      customer,
      setCustomer,
      customerDiscounts,
      listRef,
      resultCount,
      setResultCount,
      dailyRoute,
      setDailyRoute,
      startTime,
      setStartTime,
      ulistavanjeProducts,
      setUlistavanjeProducts,
      isRemoteVisit,
      setIsRemoteVisit,
      coordinates,
      setCoordinates,
    ],
  );

  return (
    <Context.Provider value={customerContext}>{children}</Context.Provider>
  );
};

export const { Consumer } = Context;

export const useCustomerContext = () => useContext(Context);
